(() => {
  if (window.atlasCartCorrectionLoaded) return;
  window.atlasCartCorrectionLoaded = true;

  const storageKey = 'atlasCartCorrectionV1';
  const maxAge = 2 * 60 * 60 * 1000;
  const root = window.Shopify?.routes?.root || '/';
  let correction = null;
  let addInFlight = false;
  let completionInFlight = false;
  let addSafetyTimer = null;

  const readSession = () => {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(storageKey));
      if (!value || Date.now() - Number(value.startedAt || 0) > maxAge) {
        window.sessionStorage.removeItem(storageKey);
        return null;
      }
      return value;
    } catch (error) {
      return null;
    }
  };

  const writeSession = (value) => {
    correction = value;
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  };

  const clearSession = () => {
    correction = null;
    addInFlight = false;
    completionInFlight = false;
    window.clearTimeout(addSafetyTimer);
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (error) {
      return;
    }
  };

  const viewportClass = () => {
    if (window.innerWidth < 750) return 'mobile';
    if (window.innerWidth < 990) return 'tablet';
    return 'desktop';
  };

  const track = (name, detail = {}) => {
    if (!window.AtlasAnalytics?.track || !correction) return false;
    return window.AtlasAnalytics.track(name, {
      product_id: String(correction.productId || ''),
      variant_id: String(correction.variantId || ''),
      cart_surface: correction.surface || '',
      viewport_class: viewportClass(),
      event_version: '1',
      ...detail,
    });
  };

  const cartJson = async () => {
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart_read_failed');
    return response.json();
  };

  const announce = (notice, message, focus = false) => {
    const status = notice?.querySelector('[data-atlas-correction-status]');
    if (!status) return;
    status.textContent = message;
    if (focus) status.focus({ preventScroll: true });
  };

  const startCorrection = async (link) => {
    if (link.dataset.atlasCorrectionBusy === 'true') return;
    link.dataset.atlasCorrectionBusy = 'true';
    link.setAttribute('aria-busy', 'true');
    const destination = link.href;
    try {
      const cart = await cartJson();
      const original = cart.items.find((item) => item.key === link.dataset.originalKey);
      if (!original) throw new Error('original_missing');
      if (String(original.product_id) !== link.dataset.productId) throw new Error('product_mismatch');
      if (String(original.variant_id) !== link.dataset.variantId) throw new Error('variant_mismatch');
      const baseline = cart.items
        .filter((item) => String(item.product_id) === link.dataset.productId && String(item.variant_id) === link.dataset.variantId)
        .map((item) => ({ key: item.key, quantity: item.quantity }));
      const token = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const stored = writeSession({
        version: 1,
        token,
        state: 'EDIT_IN_PROGRESS',
        originalKey: original.key,
        productId: String(original.product_id),
        variantId: String(original.variant_id),
        originalQuantity: original.quantity,
        startedAt: Date.now(),
        surface: link.dataset.surface || 'cart',
        baseline,
      });
      if (!stored) throw new Error('storage_failed');
      track('atlas_cart_corrected_design_start');
      window.location.assign(destination);
    } catch (error) {
      link.dataset.atlasCorrectionBusy = 'false';
      link.removeAttribute('aria-busy');
      window.location.assign(destination);
    }
  };

  const relevantLine = (item) => {
    if (!correction) return false;
    if (String(item.product_id) !== correction.productId) return false;
    if (String(item.variant_id) !== correction.variantId) return false;
    return Boolean(item.properties?._pplr_preview && item.properties?.Preview);
  };

  const renderConfirmed = (notice, replacement) => {
    correction.state = 'REPLACEMENT_CONFIRMED';
    correction.replacementKey = replacement.key;
    correction.replacementQuantity = replacement.quantity;
    writeSession(correction);
    notice.hidden = false;
    notice.dataset.state = 'confirmed';
    const actions = notice.querySelector('[data-atlas-correction-actions]');
    const remove = notice.querySelector('[data-atlas-correction-remove]');
    const quantity = notice.querySelector('[data-atlas-correction-quantity]');
    if (actions) actions.hidden = false;
    if (remove) remove.hidden = Number(correction.originalQuantity) !== 1;
    if (quantity) {
      quantity.hidden = Number(correction.originalQuantity) === 1;
      quantity.textContent = quantity.dataset.message.replace('[quantity]', String(correction.originalQuantity));
    }
    announce(notice, notice.dataset.addedMessage, true);
    track('atlas_cart_corrected_design_added');
  };

  const verifyReplacement = async (notice) => {
    const cart = await cartJson();
    const baseline = new Map((correction.baseline || []).map((item) => [item.key, Number(item.quantity)]));
    const candidates = cart.items.filter((item) => relevantLine(item) && !baseline.has(item.key));
    if (candidates.length === 1) {
      renderConfirmed(notice, candidates[0]);
      return;
    }
    const merged = cart.items.some((item) => {
      if (!relevantLine(item) || !baseline.has(item.key)) return false;
      return Number(item.quantity) > baseline.get(item.key);
    });
    if (merged) {
      notice.hidden = false;
      notice.dataset.state = 'merged';
      announce(notice, notice.dataset.mergedMessage, true);
      track('atlas_cart_corrected_design_cancel', { correction_outcome: 'identical_merge' });
      clearSession();
      return;
    }
    notice.hidden = false;
    notice.dataset.state = 'failed';
    announce(notice, notice.dataset.verifyFailedMessage, true);
    clearSession();
  };

  const completeCorrectionAdd = async () => {
    if (!correction || correction.state !== 'EDIT_IN_PROGRESS' || completionInFlight) return;
    const notice = document.querySelector('[data-atlas-correction-notice]');
    if (!notice) return;
    completionInFlight = true;
    window.clearTimeout(addSafetyTimer);
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 450));
      await verifyReplacement(notice);
    } catch (error) {
      notice.hidden = false;
      notice.dataset.state = 'failed';
      announce(notice, notice.dataset.verifyFailedMessage, true);
      clearSession();
    } finally {
      addInFlight = false;
      completionInFlight = false;
    }
  };

  const markAddAttempt = (event) => {
    if (!correction || correction.state !== 'EDIT_IN_PROGRESS') return;
    const button = event.target.closest?.('.pplr_form .p_a_t_c');
    if (!button) return;
    // The existing Zepto validation bridge runs first and prevents invalid
    // submissions. Do not lock a correction attempt that never left the form.
    if (event.defaultPrevented) return;
    if (addInFlight) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    addInFlight = true;
    button.setAttribute('aria-busy', 'true');
    addSafetyTimer = window.setTimeout(() => {
      if (!correction || correction.state !== 'EDIT_IN_PROGRESS' || completionInFlight) return;
      const notice = document.querySelector('[data-atlas-correction-notice]');
      if (notice) {
        notice.hidden = false;
        notice.dataset.state = 'failed';
        announce(notice, notice.dataset.verifyFailedMessage, true);
      }
      clearSession();
    }, 20000);
  };

  const unlockBlockedAdd = () => {
    addInFlight = false;
    window.clearTimeout(addSafetyTimer);
    document.querySelectorAll('.pplr_form .p_a_t_c[aria-busy="true"]').forEach((button) => button.removeAttribute('aria-busy'));
  };

  const removeOriginal = async (button, notice) => {
    if (!correction || correction.state !== 'REPLACEMENT_CONFIRMED') return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    announce(notice, notice.dataset.removingMessage);
    try {
      if (Number(correction.originalQuantity) !== 1) throw new Error('quantity_requires_review');
      const before = await cartJson();
      const original = before.items.find((item) => item.key === correction.originalKey);
      const replacement = before.items.find((item) => item.key === correction.replacementKey);
      if (!original || !replacement || !relevantLine(replacement)) throw new Error('ambiguous_line');
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: original.key, quantity: 0 }),
      });
      if (!response.ok) throw new Error('removal_failed');
      const after = await cartJson();
      const originalStillExists = after.items.some((item) => item.key === correction.originalKey);
      const replacementStillExists = after.items.some((item) => item.key === correction.replacementKey);
      if (originalStillExists || !replacementStillExists) throw new Error('removal_unverified');
      track('atlas_cart_corrected_design_remove_original');
      clearSession();
      notice.dataset.state = 'complete';
      notice.querySelector('[data-atlas-correction-actions]').hidden = true;
      announce(notice, notice.dataset.removedMessage, true);
    } catch (error) {
      notice.dataset.state = 'failed';
      announce(notice, error.message === 'ambiguous_line' ? notice.dataset.ambiguousMessage : notice.dataset.removalFailedMessage, true);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  const keepBoth = (notice) => {
    track('atlas_cart_corrected_design_keep_both');
    clearSession();
    notice.dataset.state = 'complete';
    notice.querySelector('[data-atlas-correction-actions]').hidden = true;
    announce(notice, notice.dataset.keepBothMessage, true);
  };

  const initializeProductNotice = async () => {
    const notice = document.querySelector('[data-atlas-correction-notice]');
    if (!notice || !correction) return;
    if (notice.dataset.productId !== correction.productId) return;
    const currentVariant = new URLSearchParams(window.location.search).get('variant') || notice.dataset.variantId;
    if (String(currentVariant) !== correction.variantId || notice.dataset.variantId !== correction.variantId) {
      notice.hidden = false;
      notice.dataset.state = 'failed';
      announce(notice, notice.dataset.variantFailedMessage, true);
      clearSession();
      return;
    }
    notice.hidden = false;
    if (correction.state === 'REPLACEMENT_CONFIRMED') {
      try {
        const cart = await cartJson();
        const replacement = cart.items.find((item) => item.key === correction.replacementKey);
        if (replacement && relevantLine(replacement)) renderConfirmed(notice, replacement);
        else throw new Error('replacement_missing');
      } catch (error) {
        announce(notice, notice.dataset.ambiguousMessage, true);
      }
    }
  };

  const cancelOnCartReturn = () => {
    if (!document.body.classList.contains('template-cart')) return;
    if (!correction || correction.state !== 'EDIT_IN_PROGRESS') return;
    track('atlas_cart_corrected_design_cancel', { correction_outcome: 'returned_to_cart' });
    clearSession();
  };

  const cancelAfterBackNavigation = () => {
    correction = readSession();
    if (!correction || correction.state !== 'EDIT_IN_PROGRESS') return;
    if (document.body.classList.contains('template-cart')) {
      cancelOnCartReturn();
      return;
    }
    const notice = document.querySelector('[data-atlas-correction-notice]');
    if (notice?.dataset.productId === correction.productId) return;
    track('atlas_cart_corrected_design_cancel', { correction_outcome: 'left_personaliser' });
    clearSession();
  };

  const initialize = () => {
    correction = readSession();
    cancelOnCartReturn();
    document.addEventListener('click', (event) => {
      const start = event.target.closest?.('[data-atlas-correction-start]');
      if (start) {
        event.preventDefault();
        startCorrection(start);
        return;
      }
      markAddAttempt(event);
      const remove = event.target.closest?.('[data-atlas-correction-remove]');
      if (remove) removeOriginal(remove, remove.closest('[data-atlas-correction-notice]'));
      const keep = event.target.closest?.('[data-atlas-correction-keep]');
      if (keep) keepBoth(keep.closest('[data-atlas-correction-notice]'));
      if (event.target.closest?.('.pplr_close') && correction?.state === 'EDIT_IN_PROGRESS') {
        track('atlas_cart_corrected_design_cancel', { correction_outcome: 'personaliser_closed' });
        const notice = document.querySelector('[data-atlas-correction-notice]');
        if (notice) announce(notice, notice.dataset.cancelledMessage, true);
        clearSession();
      }
    }, true);
    window.addEventListener('atlasPersonaliserValidationBlocked', unlockBlockedAdd);
    window.addEventListener('pplrAddToCartCompleted', completeCorrectionAdd);
    window.addEventListener('pageshow', cancelAfterBackNavigation);
    initializeProductNotice();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
